// Modal opened from the per-row "生成登录信息" action button on /profiles
// and /admin/profiles. Triggers POST /profiles/:id/generate-login and
// renders the returned email / temporary password / login URL with copy
// buttons for each. Designed to be self-contained — no shared modal shell.

import React, { useEffect, useState } from 'react';
import { X as XIcon, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { generateLoginForProfile, copyToClipboard } from '../../utils/profileShare';

export default function GenerateLoginModal({ profile, isZh = true, onClose }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Backdrop click closes (target===currentTarget keeps clicks on the card
  // from collapsing the modal — matches the kpi-v2 modal pattern).
  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await generateLoginForProfile(profile.id);
      setData(r);
    } catch (e) {
      setError(e?.response?.data?.error || (isZh ? '生成失败' : 'Failed'));
    } finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [profile.id]);

  const copyOne = async (text, label) => {
    const ok = await copyToClipboard(text);
    if (ok) toast.success(`${label}${t('已复制', ' copied')}`);
    else toast.error(t('复制失败', 'Copy failed'));
  };
  const copyAll = async () => {
    if (!data) return;
    const block = [
      `${t('登录地址', 'Login URL')}: ${data.login_url || ''}`,
      `${t('邮箱', 'Email')}: ${data.email || ''}`,
      `${t('密码', 'Password')}: ${data.password || ''}`,
    ].join('\n');
    const ok = await copyToClipboard(block);
    if (ok) toast.success(t('已复制全部', 'All copied'));
    else toast.error(t('复制失败', 'Copy failed'));
  };

  const profileName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || `#${profile?.id}`;

  const row = ({ label, value, mono }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6E685F', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input value={value || ''} readOnly
          style={{
            flex: 1, padding: '8px 10px', border: '1px solid #E4DFD7', borderRadius: 2,
            fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit', background: '#F6F3EE',
            color: '#111111',
          }} />
        <button type="button" onClick={() => copyOne(value, label)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '8px 12px', borderRadius: 2, fontSize: 12, fontWeight: 600,
            background: '#93591A', color: '#FFFDF9', border: '1px solid #93591A', cursor: 'pointer',
          }}>
          <Copy size={12} /> {t('复制', 'Copy')}
        </button>
      </div>
    </div>
  );

  return (
    <div onMouseDown={onBackdrop}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 22000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#FFFDF9', borderRadius: 2, width: 'min(520px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'none', position: 'relative' }}>
        <button type="button" onClick={onClose}
          style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: '#6E685F', cursor: 'pointer', zIndex: 1 }}>
          <XIcon size={18} />
        </button>

        <div style={{ padding: '16px 20px 10px', borderBottom: '1px solid #EFEAE2' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111111' }}>{t('生成客户登录信息', 'Generate client login')}</div>
          <div style={{ fontSize: 11, color: '#6E685F', marginTop: 2 }}>{profileName}</div>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#6E685F' }}>
              <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
              <div style={{ fontSize: 13 }}>{t('生成中…', 'Generating…')}</div>
            </div>
          ) : error ? (
            <div style={{ padding: 14, color: '#9E2B25', fontSize: 13, background: '#F5E6E4', borderRadius: 2 }}>{error}</div>
          ) : data ? (
            <>
              <div style={{ padding: 10, marginBottom: 14, background: '#E7EFE9', border: '1px solid #C9DCCF', borderRadius: 2, fontSize: 12, color: '#2F6146' }}>
                {t('已生成临时密码。请尽快分享给客户;客户首次登录后可自行修改。', 'Temporary password generated. Share with the client; they can change it after first login.')}
              </div>
              {row({ label: t('登录地址', 'Login URL'), value: data.login_url, mono: true })}
              {row({ label: t('邮箱', 'Email'), value: data.email, mono: true })}
              {row({ label: t('密码', 'Password'), value: data.password, mono: true })}
            </>
          ) : null}
        </div>

        <div style={{ padding: '10px 18px 14px', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <button type="button" onClick={run} disabled={loading}
            style={{ padding: '6px 14px', borderRadius: 2, border: '1px solid #E4DFD7', background: '#FFFDF9', color: '#3A3835', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={11} /> {t('重新生成', 'Regenerate')}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {data && (
              <button type="button" onClick={copyAll}
                style={{ padding: '6px 14px', borderRadius: 2, border: '1px solid #111111', background: '#111111', color: '#FFFDF9', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Copy size={11} /> {t('复制全部', 'Copy all')}
              </button>
            )}
            <button type="button" onClick={onClose}
              style={{ padding: '6px 14px', borderRadius: 2, border: '1px solid #E4DFD7', background: '#FFFDF9', color: '#3A3835', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {t('关闭', 'Close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
